import type {
  CoachingSession,
  OutputLink,
  PlaybookOutputFile,
  SessionPrep,
  WorkSubmission,
} from '@doneverse/shared'
import { api } from '../api/index.js'

/** How hard the doer found the task — drives coaching emphasis. */
export type SelfRatedChallenge = 'low' | 'medium' | 'high'

/**
 * The pre-session payload a doer submits before an AI Doer Success Coach
 * session. The agent reads this at session open (ClickUp epic 86d36n1zr).
 *
 * `outputs` and `founderCommsScreenshot` are optional because they are
 * suppressed when the work already flowed through the Hub — see
 * `usePreSessionHubSignal` on the dashboard side.
 */
export interface PreSessionPrepPayload {
  taskSummary: string
  outputs?: {
    files: PlaybookOutputFile[]
    links: OutputLink[]
    brief: string
    feedback: string
  }
  founderCommsScreenshot?: PlaybookOutputFile | null
  challengeLevel: SelfRatedChallenge
  challengeNotes?: string
  /** Empty when the doer explicitly selected the "did not work on any stages" option. */
  blueprintStepIds: string[]
}

export interface PreSessionPrepResponse {
  session: CoachingSession
  prep: SessionPrep
  resumed: boolean
}

/**
 * Pre-fill suggestions for the prep form, derived from the doer's work since
 * their last session. The form drops these into still-empty fields so the doer
 * reviews rather than retypes. `outputs.files` are already-uploaded references
 * (no re-upload needed); `outputs.links` are plain URLs. `workSubmissions` are
 * the independent submit-work rows in the same review window.
 */
export interface PrepSuggestionsResponse {
  since: string
  taskSummary: string
  workSubmissions: WorkSubmission[]
  outputs: {
    files: PlaybookOutputFile[]
    links: OutputLink[]
    brief: string
  }
}

export const preSessionService = {
  // POST the prep to the doer's open coaching session (creates one on first
  // submit, overwrites the prep on re-submit). Workspace comes from the
  // x-doneverse-workspace-id header the api client injects. The returned
  // session is the same open session the AI coach resumes on /ai-coaching.
  submit: async (payload: PreSessionPrepPayload): Promise<PreSessionPrepResponse> => {
    return api.post(payload, '/v1/coaching-sessions').json<PreSessionPrepResponse>()
  },

  // Pre-fill data for the form: tasks completed + outputs submitted since the
  // doer's last session. Workspace comes from the x-doneverse-workspace-id
  // header the api client injects.
  fetchSuggestions: async (): Promise<PrepSuggestionsResponse> => {
    return api.get('/v1/coaching-sessions/prep-suggestions').json<PrepSuggestionsResponse>()
  },
}
